// JS port of the DPS simulation from 05-generate-move-combos.py
// Adds type effectiveness and weather boost multipliers on top of STAB + shadow

const CPM_L50 = 0.84029999;
const CPM_L40 = 0.79030001;
const ATK_IV = 15;
const SHADOW_MULT = 1.2;
const STAB_MULT = 1.2;
const SIM_SECONDS = 300.0;
const MAX_CHARGED_QUEUE = 1000;

/**
 * Raid per-hit damage formula:
 *   floor(0.5 * power * (A/D) * multiplier) + 1
 */
function raidDamagePerUse(power, attackerAtk, defenderDef, multiplier) {
  if (!power || power <= 0 || attackerAtk <= 0 || defenderDef <= 0) return null;
  return Math.floor(0.5 * power * (attackerAtk / defenderDef) * multiplier) + 1;
}

/**
 * Discrete raid DPS simulation.
 * Uses fast moves until enough energy for charged, then uses charged.
 * Repeats for SIM_SECONDS.
 *
 * @param {Object} params
 * @param {number} params.fastPower
 * @param {number} params.fastDurationS - in seconds
 * @param {number} params.fastEnergyGain
 * @param {number} params.chargedPower
 * @param {number} params.chargedDurationS - in seconds
 * @param {number} params.chargedEnergyCost - positive number
 * @param {number} params.attackerAtk - computed attack stat
 * @param {number} params.defenderDef - computed defense stat
 * @param {number} params.fastMultiplier - STAB * shadow * typeEff * weather for fast
 * @param {number} params.chargedMultiplier - STAB * shadow * typeEff * weather for charged
 * @returns {number|null} DPS value
 */
function simulateRaidDps({
  fastPower, fastDurationS, fastEnergyGain,
  chargedPower, chargedDurationS, chargedEnergyCost,
  attackerAtk, defenderDef,
  fastMultiplier, chargedMultiplier
}) {
  if (!fastDurationS || fastDurationS <= 0 || !chargedDurationS || chargedDurationS <= 0) return null;
  if (!fastEnergyGain || fastEnergyGain <= 0 || !chargedEnergyCost || chargedEnergyCost <= 0) return null;

  const fastDmg = raidDamagePerUse(fastPower, attackerAtk, defenderDef, fastMultiplier);
  const chargedDmg = raidDamagePerUse(chargedPower, attackerAtk, defenderDef, chargedMultiplier);
  if (fastDmg === null || chargedDmg === null) return null;

  let t = 0.0;
  let energy = 0.0;
  let totalDamage = 0.0;
  let chargedUsed = 0;

  while (t < SIM_SECONDS) {
    if (energy >= chargedEnergyCost) {
      totalDamage += chargedDmg;
      energy -= chargedEnergyCost;
      t += chargedDurationS;
      chargedUsed++;
      if (chargedUsed > MAX_CHARGED_QUEUE) break;
    } else {
      totalDamage += fastDmg;
      energy += fastEnergyGain;
      t += fastDurationS;
    }
  }

  return t > 0 ? totalDamage / t : null;
}

/**
 * Calculate raid DPS for one Pokémon + moveset against a specific boss.
 *
 * @param {Object} params
 * @param {number} params.baseAttack - Pokémon's base attack stat
 * @param {string[]} params.pokemonTypes - Pokémon's type(s) for STAB calc
 * @param {boolean} params.isShadow - whether this is a shadow Pokémon
 * @param {Object} params.fastMove - { power, duration (ms), energy_delta, type }
 * @param {Object} params.chargedMove - { power, duration (ms), energy_delta (negative), type }
 * @param {number} params.bossDefense - Boss's base_defense stat
 * @param {number} params.fastTypeEff - type effectiveness multiplier for fast move vs boss
 * @param {number} params.chargedTypeEff - type effectiveness multiplier for charged move vs boss
 * @param {number} params.fastWeatherBoost - weather boost for fast move (1.0 or 1.2)
 * @param {number} params.chargedWeatherBoost - weather boost for charged move (1.0 or 1.2)
 * @returns {{ dpsL50: number, dpsL40: number }|null}
 */
export function calculateRaidDps({
  baseAttack, pokemonTypes, isShadow,
  fastMove, chargedMove,
  bossDefense,
  fastTypeEff, chargedTypeEff,
  fastWeatherBoost, chargedWeatherBoost
}) {
  const shadowMult = isShadow ? SHADOW_MULT : 1.0;
  const fastStab = pokemonTypes.includes(fastMove.type) ? STAB_MULT : 1.0;
  const chargedStab = pokemonTypes.includes(chargedMove.type) ? STAB_MULT : 1.0;

  const attackerAtk = (baseAttack + ATK_IV) * CPM_L50;
  const defenderDef = (bossDefense + 0) * CPM_L50; // 0 DEF IV for standardization

  const fastMultiplier = fastStab * shadowMult * fastTypeEff * fastWeatherBoost;
  const chargedMultiplier = chargedStab * shadowMult * chargedTypeEff * chargedWeatherBoost;

  const fastDurationS = fastMove.duration / 1000;
  const chargedDurationS = chargedMove.duration / 1000;
  const chargedEnergyCost = Math.abs(chargedMove.energy_delta);

  const fastEnergyGain = fastMove.energy_delta != null
    ? fastMove.energy_delta
    : (fastMove.eps != null && fastDurationS > 0 ? fastMove.eps * fastDurationS : null);

  if (fastEnergyGain == null || fastEnergyGain <= 0) return null;

  const dpsL50 = simulateRaidDps({
    fastPower: fastMove.power,
    fastDurationS,
    fastEnergyGain,
    chargedPower: chargedMove.power,
    chargedDurationS,
    chargedEnergyCost,
    attackerAtk,
    defenderDef,
    fastMultiplier,
    chargedMultiplier,
  });

  if (dpsL50 === null) return null;

  // Scale L40 DPS proportionally
  const dpsL40 = dpsL50 * (CPM_L40 / CPM_L50);

  return {
    dpsL50: Math.round(dpsL50 * 10) / 10,
    dpsL40: Math.round(dpsL40 * 10) / 10,
  };
}
